/**
 * Sprint 111 — Cuánto cobrar de envío, según qué tan lejos vive el cliente.
 *
 * ## De dónde sale
 *
 * El negocio describió su realidad así: *"es por zona (en kilómetros)… Galaxia, Tabasco 2000 desde
 * mi negocio son 50 pesos"*, y en la misma frase puso su regla: *"aunque siempre por regla prefiero
 * cotizar"*. El 2026-08-26 el dueño explicó **por qué** cotiza: le manda la ubicación del cliente al
 * grupo de motomandados y **ellos** dicen el costo. El precio final no es del sistema.
 *
 * Por eso esto **sugiere y jamás aplica solo**. Es un atajo para no teclear el mismo monto cuarenta
 * veces al mes, no una tarifa automática.
 *
 * ## Por qué la sección "Zonas y tarifas" estaba vacía
 *
 * Se le pidió al negocio que enumerara **colonias**. Nadie sabe cuántas colonias hay en
 * Villahermosa, así que no llenó ninguna y la función lleva meses muerta. Con **rangos de
 * kilómetros** son tres o cuatro renglones y se escriben en dos minutos.
 *
 * ## Por qué no se usa un servicio de rutas
 *
 * Un enrutador real (Google Directions, OSRM alojado) daría la distancia de manejo exacta, pero
 * cuesta por consulta, mete latencia en el peor momento del flujo y —lo que decide— **manda la
 * dirección de los clientes del negocio a un tercero**, que es justo lo que DT-063 dice que no
 * tenemos acordado con nadie. Para elegir entre "$40 o $50" no hace falta esa precisión.
 *
 * Se usa haversine (línea recta) con un **factor de rodeo**: el estándar de facto para convertir
 * distancia en línea recta a distancia de calle en zona urbana. Cero dependencias, cero costo, y
 * la ubicación del cliente no sale del servidor.
 */

/**
 * Cuánto más largo es el camino real que la línea recta, en ciudad.
 *
 * 1.3 es el valor habitual para trazas urbanas mixtas. No pretende ser exacto: el número que
 * importa es el que dice el motomandado, y esto sólo elige entre dos o tres renglones de tarifa.
 */
export const FACTOR_RODEO_URBANO = 1.3;

const RADIO_TIERRA_KM = 6371;

const aRadianes = (grados: number) => (grados * Math.PI) / 180;

export interface Punto {
  latitud: number;
  longitud: number;
}

/**
 * Lo que llega de la base o de un formulario: los campos existen pero pueden venir vacíos.
 * Se acepta `null` además de `undefined` porque Prisma devuelve `null`, no `undefined`.
 */
export type PuntoQuiza = { latitud?: number | null; longitud?: number | null };

/** ¿Este par de coordenadas es un punto usable, o basura / un campo sin llenar? */
export function esPuntoValido(p: PuntoQuiza | null | undefined): p is Punto {
  if (!p) return false;
  const { latitud, longitud } = p;
  if (typeof latitud !== 'number' || typeof longitud !== 'number') return false;
  if (!Number.isFinite(latitud) || !Number.isFinite(longitud)) return false;
  if (latitud < -90 || latitud > 90) return false;
  if (longitud < -180 || longitud > 180) return false;
  /**
   * (0, 0) es la Isla Nula, en el Atlántico. Nadie pide asados desde ahí: en la práctica significa
   * "el campo se guardó vacío y alguien lo leyó como cero". Tratarlo como punto válido produciría
   * una distancia de miles de kilómetros y una sugerencia absurda con cara de dato.
   */
  if (latitud === 0 && longitud === 0) return false;
  return true;
}

/** Distancia en línea recta, en kilómetros. */
export function distanciaLineaRectaKm(a: Punto, b: Punto): number {
  const dLat = aRadianes(b.latitud - a.latitud);
  const dLon = aRadianes(b.longitud - a.longitud);
  const lat1 = aRadianes(a.latitud);
  const lat2 = aRadianes(b.latitud);

  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * RADIO_TIERRA_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Distancia estimada **de calle**, que es la que le importa a quien maneja la moto.
 *
 * Devuelve `null` si falta cualquiera de los dos puntos: **sin origen no hay distancia**, y el
 * origen es el negocio. Preferimos no sugerir nada a sugerir desde un punto equivocado — un origen
 * mal puesto por tres cuadras hace que *todas* las tarifas salgan mal, siempre, en silencio.
 */
export function distanciaEnvioKm(
  negocio: PuntoQuiza | null | undefined,
  cliente: PuntoQuiza | null | undefined,
): number | null {
  if (!esPuntoValido(negocio) || !esPuntoValido(cliente)) return null;
  return distanciaLineaRectaKm(negocio, cliente) * FACTOR_RODEO_URBANO;
}

export interface ZonaConRango {
  id: number;
  nombre: string;
  /** Sprint 39 — Centavos. */
  tarifa: number;
  activa: boolean;
  kmDesde: number | null;
  kmHasta: number | null;
}

export interface SugerenciaEnvio {
  zona: ZonaConRango;
  distanciaKm: number;
}

/**
 * Qué zona le toca a esa distancia.
 *
 * Reglas, y cada una tiene su motivo:
 * - Sólo zonas **activas**: una zona apagada es una tarifa que el negocio dejó de dar.
 * - Sólo zonas con **rango declarado**: las que el negocio capturó por nombre ("Galaxia") siguen
 *   sirviendo como atajo manual en la pantalla, pero no pueden elegirse solas — nadie dijo a qué
 *   distancia corresponden.
 * - El rango es `[kmDesde, kmHasta)`: cerrado abajo, abierto arriba. Así `0–3` y `3–6` no se pelean
 *   por el kilómetro 3, que es exactamente el borde donde un cliente reclamaría.
 * - `kmHasta: null` = "de aquí en adelante". Le sirve al negocio para el último renglón.
 * - Si dos zonas se traslapan gana **la más barata**. Es una decisión de negocio, no técnica: ante
 *   una configuración ambigua, el error que se perdona es cobrar de menos.
 */
export function sugerirZonaPorDistancia(
  zonas: ZonaConRango[],
  distanciaKm: number | null,
): SugerenciaEnvio | null {
  if (distanciaKm === null || !Number.isFinite(distanciaKm) || distanciaKm < 0) return null;

  const candidatas = zonas
    .filter((z) => z.activa)
    .filter((z) => z.kmDesde !== null)
    .filter((z) => distanciaKm >= (z.kmDesde as number))
    .filter((z) => z.kmHasta === null || distanciaKm < z.kmHasta);

  if (candidatas.length === 0) return null;

  const elegida = candidatas.reduce((mejor, z) => (z.tarifa < mejor.tarifa ? z : mejor));
  return { zona: elegida, distanciaKm };
}

/** Cómo se le dice al operador, sin decimales que nadie necesita y sin fingir precisión. */
export function textoDistancia(distanciaKm: number): string {
  if (distanciaKm < 1) return 'menos de 1 km';
  return `~${distanciaKm.toFixed(1)} km`;
}
